"use client";

import Link from "next/link";
import { useState, useEffect, useSyncExternalStore } from "react";
import SdgTooltipList from "@/components/ui/SdgTooltip";
import PaymentTooltip from "@/components/ui/PaymentTooltip";
import ModernDialog from "@/components/ui/ModernDialog";
import InvitationGrid from "@/components/ui/InvitationGrid";
import InvitationFeatures from "@/components/ui/InvitationFeatures";
import { useAuth } from "@/context/AuthContext";
import { paymentMethods, sdgProgram } from "@/constants/homeData";
import Header from "../ui/Header";
import { post } from "@/lib/api";

function PhoneIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4 shrink-0"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4 shrink-0"
    >
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4 shrink-0"
    >
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

export default function MobileHome({ invitations = [] }) {
  const contactEmail = "hello" + "@" + "dreamwedding.id";
  const [dialogType, setDialogType] = useState(null);
  const [consultationForm, setConsultationForm] = useState({
    name: '',
    contact: '',
    contactType: 'email'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const showContactEmail = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );
  const { showToast } = useAuth();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.dropdown-container')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  const handleConsultationSubmit = async () => {
    if (!consultationForm.name.trim() || !consultationForm.contact.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await post("/consultations", {
        name: consultationForm.name,
        contact: consultationForm.contact,
        contact_type: consultationForm.contactType
      }, { useApiKey: true });

      setDialogType(null);
      setConsultationForm({ name: '', contact: '', contactType: 'email' });
      showToast("Consultation request submitted successfully! We will contact you soon.");
    } catch (error) {
      console.error('Failed to submit consultation request:', error);
      showToast("Failed to submit consultation request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="bg-background relative">
      <ModernDialog
        isOpen={dialogType === "consultation"}
        onClose={() => setDialogType(null)}
        title="Request Consultation"
        description="Connect with our premier planners—simply share your contact info and we’ll be in touch."
      >
        <div className="flex flex-col gap-4">
          <label htmlFor="consultation-name" className="sr-only">Your Name</label>
          <input
            id="consultation-name"
            name="name"
            autoComplete="name"
            type="text"
            placeholder="Your Name"
            value={consultationForm.name}
            onChange={(e) => setConsultationForm(prev => ({ ...prev, name: e.target.value }))}
            className="w-full rounded-xl border border-primary-light px-4 py-3 text-sm focus:border-primary focus:outline-none"
          />
          <div className="flex gap-2">
            <div className="relative dropdown-container">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center justify-between rounded-xl border border-primary-light px-3 py-3 text-sm focus:border-primary focus:outline-none bg-white min-w-25"
              >
                <span className="flex items-center gap-2">
                  {consultationForm.contactType === 'email' ? (
                    <>
                      <MailIcon />
                      Email
                    </>
                  ) : (
                    <>
                      <PhoneIcon />
                      Phone
                    </>
                  )}
                </span>
                <svg
                  className={`h-4 w-4 text-zinc-400 ml-2 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-primary-light rounded-xl shadow-lg z-10">
                  <button
                    type="button"
                    onClick={() => {
                      setConsultationForm(prev => ({ ...prev, contactType: 'email' }));
                      setIsDropdownOpen(false);
                    }}
                    className="flex items-center gap-2 w-full px-3 py-3 text-sm hover:bg-primary/5 first:rounded-t-xl"
                  >
                    <MailIcon />
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setConsultationForm(prev => ({ ...prev, contactType: 'phone' }));
                      setIsDropdownOpen(false);
                    }}
                    className="flex items-center gap-2 w-full px-3 py-3 text-sm hover:bg-primary/5 rounded-b-xl"
                  >
                    <PhoneIcon />
                    Phone
                  </button>
                </div>
              )}
            </div>
            {consultationForm.contactType === 'email' ? (
              <>
              <label htmlFor="consultation-contact" className="sr-only">Email Address</label>
              <input
                id="consultation-contact"
                name="contact"
                autoComplete="email"
                type="email"
                placeholder="Email Address"
                value={consultationForm.contact}
                onChange={(e) => setConsultationForm(prev => ({ ...prev, contact: e.target.value }))}
                className="flex-1 rounded-xl border border-primary-light px-4 py-3 text-sm focus:border-primary focus:outline-none"
              />
              </>
            ) : (
              <>
              <label htmlFor="consultation-contact" className="sr-only">Phone Number</label>
              <div className="flex items-center gap-0 overflow-hidden rounded-xl border border-primary-light bg-white focus-within:border-primary transition flex-1">
                <span className="border-r border-primary-light px-3 py-3 text-sm text-zinc-400 select-none bg-zinc-50">
                  +62
                </span>
                <input
                  id="consultation-contact"
                  name="contact"
                  autoComplete="tel"
                  type="tel"
                  inputMode="numeric"
                  placeholder="8xx-xxxx-xxxx"
                  value={consultationForm.contact}
                  onChange={(e) => setConsultationForm(prev => ({ ...prev, contact: e.target.value }))}
                  className="flex-1 bg-transparent px-3 py-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none"
                />
              </div>
              </>
            )}
          </div>
          <button
            onClick={handleConsultationSubmit}
            disabled={isSubmitting || !consultationForm.name.trim() || !consultationForm.contact.trim()}
            className="rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-primary-dark mt-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </>
            ) : (
              'Send Request'
            )}
          </button>
        </div>
      </ModernDialog>

      {/* Mobile Header */}
      <Header />

      {/* Main Content */}
      <main className="flex flex-col gap-10 px-4 py-6">
        {/* Hero Section */}
        <section className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <h1 className="font-cormorant text-4xl font-semibold leading-tight text-zinc-900">
              Undangan Digital{" "}
              <span className="text-primary italic">Cantik </span> &amp;
              Berkesan
            </h1>
            <p className="text-base text-zinc-600 leading-relaxed">
              Pilih template impianmu, personalisasi sesuai kisah cintamu, dan
              bagikan ke semua tamu dalam hitungan menit.
            </p>
            <div className="flex items-center gap-4 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3">
              <div>
                <p className="text-xs text-zinc-500 font-medium">
                  Harga mulai dari
                </p>
                <p className="font-cormorant text-2xl font-semibold text-primary-dark">
                  Rp 75.000
                </p>
              </div>
              <div className="h-10 w-px bg-primary/20" />
              <div>
                <p className="text-xs text-zinc-500 font-medium">
                  Aktif selama
                </p>
                <p className="text-sm font-semibold text-zinc-800">
                  6 Bulan Gratis
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-3 mt-1">
              <button
                onClick={() => scrollToSection("mobile-templates")}
                className="text-center rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-white shadow-sm active:bg-primary-dark transition"
              >
                Pilih Template
              </button>
              <button
                onClick={() => setDialogType("consultation")}
                className="text-center rounded-full border border-primary-light px-6 py-3.5 text-sm font-semibold text-primary-dark active:bg-primary-light transition"
              >
                Konsultasi Gratis →
              </button>
            </div>
          </div>

          <InvitationFeatures />
        </section>

        {/* Template Library */}
        <section id="mobile-templates" className="flex flex-col gap-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">
              Template Library
            </p>
            <h2 className="font-cormorant text-3xl font-semibold text-zinc-900 mt-2">
              Latest collections
            </h2>
          </div>
          <InvitationGrid invitations={invitations} />
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-primary-light bg-primary-light p-6 pb-10 pt-8">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-5">
            <div>
              <p className="font-cormorant text-3xl font-normal text-primary">
                Dream Wedding.
              </p>
              <p className="mt-1 text-sm text-zinc-700">
                Where Dream Weddings Begin!
              </p>
            </div>
            <div className="flex flex-col gap-2 text-sm text-zinc-800 font-medium">
              <a href="tel:+6285117337456" className="flex items-center gap-3">
                <PhoneIcon /> +62 851-1733-7456
              </a>
              <span className="flex items-center gap-3">
                <InstagramIcon /> @dreamwedding.official
              </span>
              <a
                href={showContactEmail ? `mailto:${contactEmail}` : undefined}
                className="flex items-center gap-3"
              >
                <MailIcon /> {showContactEmail ? contactEmail : "Email"}
              </a>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
              Support and Participate (SDGs)
            </p>
            <SdgTooltipList goals={sdgProgram} />
          </div>

          <div className="flex flex-col gap-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
              Your Payment Choices
            </p>
            <div className="marquee rounded-xl bg-white/40 p-2">
              <div className="marquee-fade marquee-fade-left" />
              <div className="marquee-fade marquee-fade-right" />
              <div className="marquee-track">
                {[...paymentMethods.filter(method => method.value !== 'other'), ...paymentMethods.filter(method => method.value !== 'other')].map((method, index) => (
                  <PaymentTooltip
                    key={`${method.name}-mobile-${index}`}
                    method={method}
                    width={112}
                    height={52}
                    className="h-12 w-auto rounded bg-white px-2.5 py-1.5 shadow-sm"
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-6 border-t border-primary-light pt-3 text-xs text-zinc-600 text-center">
            <div className="flex justify-center gap-6 font-medium">
              <Link href="/privacy-policy" className="underline">
                Privacy Policy
              </Link>
              <Link href="/terms-conditions" className="underline">
                Terms &amp; Conditions
              </Link>
            </div>
            <p className="leading-relaxed">
              &copy; {new Date().getFullYear()}, Dream Wedding Official. <br />
              Supported by{" "}
              <a
                href="https://meis.cloud"
                target="_blank"
                className="font-bold text-zinc-800 underline"
              >
                MEIS Oeuvre Corp
              </a>{" "}
              <br />
              Powered by{" "}
              <a
                href="https://ignity.co"
                target="_blank"
                className="font-bold text-zinc-800 underline"
              >
                Ignity.co
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
